import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view above the viewport.
 * Submits the real product form on click.
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetButton = null;

  /** @type {number | undefined} */
  #resetTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#abortController.abort();
    clearTimeout(this.#resetTimeout);
  }

  #setup() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    const buyButtonsBlock = section.querySelector('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#targetButton = /** @type {HTMLButtonElement | null} */ (section.querySelector('[ref="addToCartButton"]'));

    // Show sticky bar when buy buttons scroll above the viewport
    this.#buyButtonsObserver = new IntersectionObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) this.#show();
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hide();
      }
    });

    // Hide sticky bar when footer enters view
    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hide();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#show();
          }
        }
      },
      { rootMargin: '200px 0px 0px 0px' }
    );

    this.#buyButtonsObserver.observe(buyButtonsBlock);
    this.#footerObserver.observe(footer);

    const { signal } = this.#abortController;

    // Keep target button reference current across variant swaps
    section.addEventListener(
      ThemeEvents.variantUpdate,
      (event) => {
        const customEvent = /** @type {CustomEvent} */ (event);
        if (customEvent.detail.data.productId !== this.dataset.productId) return;

        const newStickyEl = customEvent.detail.data.html?.querySelector('sticky-add-to-cart');
        if (!newStickyEl) return;

        const newBar = newStickyEl.querySelector('[ref="stickyBar"]');
        if (!newBar) return;

        const stickyBar = /** @type {HTMLElement} */ (this.refs.stickyBar);
        const wasStuck = stickyBar.dataset.stuck;
        morph(stickyBar, newBar, { childrenOnly: true });
        stickyBar.dataset.stuck = wasStuck;

        const variantAvailable = newStickyEl.dataset.variantAvailable;
        if (variantAvailable !== undefined) this.dataset.variantAvailable = variantAvailable;

        this.#targetButton = /** @type {HTMLButtonElement | null} */ (section.querySelector('[ref="addToCartButton"]'));
      },
      { signal }
    );
  }

  /** Click handler wired via on:click="/handleAddToCartClick" in the template */
  handleAddToCartClick = () => {
    if (this.#targetButton?.disabled) return;

    const section = this.closest('.shopify-section');
    const form = /** @type {HTMLFormElement | null} */ (section?.querySelector('form[data-type="add-to-cart-form"]'));
    if (!form) return;

    // Always submit with subscription plan. If the subscription widget already set the
    // selling_plan input (subscribe radio is checked), use it. Otherwise inject it
    // temporarily from the plan dropdown or the first plan on the product.
    let injectedInput = /** @type {HTMLInputElement | null} */ (null);
    if (!form.querySelector('input[name="selling_plan"]')) {
      const planSelect = /** @type {HTMLSelectElement | null} */ (section?.querySelector('[data-plan-select]'));
      const planId = planSelect?.value || this.dataset.firstPlanId;
      if (planId) {
        injectedInput = document.createElement('input');
        injectedInput.type = 'hidden';
        injectedInput.name = 'selling_plan';
        injectedInput.value = planId;
        form.appendChild(injectedInput);
      }
    }

    // requestSubmit fires the submit event synchronously, allowing product-form-component
    // to read FormData before we remove the injected input below.
    form.requestSubmit();
    injectedInput?.remove();

    const btn = /** @type {HTMLButtonElement} */ (this.refs.addToCartButton);
    btn.dataset.added = 'true';
    clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      btn.removeAttribute('data-added');
    }, 2000);
  };

  #show() {
    this.#isStuck = true;
    /** @type {HTMLElement} */ (this.refs.stickyBar).dataset.stuck = 'true';
  }

  #hide() {
    this.#isStuck = false;
    /** @type {HTMLElement} */ (this.refs.stickyBar).dataset.stuck = 'false';
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
